import type { Flight, RosterEntry, User } from '../types';
import { getDaysInMonth, toDateStr, parseISO, eachDayOfInterval } from './dates';

/**
 * Build a daily roster from flights for a given month.
 * Logic:
 * 1. Flight days: the day you fly
 * 2. Layover days: days between an outbound flight arriving at city X
 *    and a return flight departing from city X
 * 3. Free days: any remaining days (you're at your base)
 */
export function buildRoster(
  flights: Flight[],
  user: User,
  month: number,
  year: number
): RosterEntry[] {
  const daysInMonth = getDaysInMonth(year, month);
  const roster: Map<string, RosterEntry> = new Map();

  // Sort flights by date
  const sortedFlights = [...flights].sort((a, b) => a.date.localeCompare(b.date));

  // Find flight pairs (outbound + return) to compute layovers
  // A pair: flight A arrives at city X, then flight B departs from city X
  const usedFlights = new Set<string>();
  const layoverPeriods: Array<{
    city: string;
    startDate: string;
    endDate: string;
    outboundFlightId: string;
    returnFlightId: string;
  }> = [];

  for (let i = 0; i < sortedFlights.length; i++) {
    const outbound = sortedFlights[i];
    if (usedFlights.has(outbound.id)) continue;

    // Find the next flight departing from the arrival city
    for (let j = i + 1; j < sortedFlights.length; j++) {
      const returnFlight = sortedFlights[j];
      if (usedFlights.has(returnFlight.id)) continue;

      if (returnFlight.departureAirport === outbound.arrivalAirport) {
        // Don't pair if it would create a "layover" at the user's base airport.
        // A return-to-base (X→DXB) followed by outbound-from-base (DXB→Y) is
        // NOT a layover — it's just being home between trips. Skipping this
        // lets the real outbound (DXB→Y) pair with its actual return (Y→DXB).
        if (outbound.arrivalAirport === user.baseAirport) {
          continue;
        }
        usedFlights.add(outbound.id);
        usedFlights.add(returnFlight.id);

        // Layover starts the day after outbound, ends the day before return
        // But actually, if outbound arrives Mar 5 and return departs Mar 10,
        // layover days are Mar 5 (arrival day) through Mar 10 (departure day)
        // We mark the flight days separately, layover = days between
        const outDate = parseISO(outbound.date);
        const retDate = parseISO(returnFlight.date);

        if (retDate > outDate) {
          // Layover: day after outbound through day before return
          const layoverStart = new Date(outDate);
          layoverStart.setDate(layoverStart.getDate() + 1);
          const layoverEnd = new Date(retDate);
          layoverEnd.setDate(layoverEnd.getDate() - 1);

          if (layoverEnd >= layoverStart) {
            layoverPeriods.push({
              city: outbound.arrivalAirport,
              startDate: toDateStr(layoverStart),
              endDate: toDateStr(layoverEnd),
              outboundFlightId: outbound.id,
              returnFlightId: returnFlight.id,
            });
          }
        }
        break;
      }
    }
  }

  // Mark flight days
  for (const flight of sortedFlights) {
    if (daysInMonth.includes(flight.date)) {
      roster.set(flight.date, {
        date: flight.date,
        type: 'flight',
        city: flight.departureAirport, // you start the day at departure airport
        flight,
      });
    }
  }

  // Mark layover days
  for (const layover of layoverPeriods) {
    const start = parseISO(layover.startDate);
    const end = parseISO(layover.endDate);
    const layoverDays = eachDayOfInterval({ start, end });
    const totalDays = layoverDays.length;

    layoverDays.forEach((day, index) => {
      const dateStr = toDateStr(day);
      if (daysInMonth.includes(dateStr) && !roster.has(dateStr)) {
        roster.set(dateStr, {
          date: dateStr,
          type: 'layover',
          city: layover.city,
          layoverDay: index + 1,
          layoverTotal: totalDays,
          pairedFlightId: layover.outboundFlightId,
        });
      }
    });
  }

  // Fill remaining days as "free" at base
  for (const dateStr of daysInMonth) {
    if (!roster.has(dateStr)) {
      roster.set(dateStr, {
        date: dateStr,
        type: 'free',
        city: user.baseAirport,
      });
    }
  }

  return daysInMonth.map((d) => roster.get(d)!);
}
